import type { Job } from '@/domains/jobs/components/JobCard';
import { addDays, format } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  MemberCapacity,
  TeamCapacitySummary,
  TeamDailyCapacity,
} from '../../hooks/useTeamCapacities';
import { detectSchedulingConflicts } from '../../utils/conflictDetector';
import type { CalendarTeam } from '../CalendarView';
import { GanttHeader } from './GanttHeader';
import { GanttResourceRow } from './GanttResourceRow';
import { GanttScrollProvider } from './GanttScrollContext';

interface GanttViewProps {
  jobs: Job[];
  teams: CalendarTeam[];
  onUpdateJob: (jobId: string, updatedJob: Partial<Job>) => void;
  onJobClick?: (jobId: string) => void;
  onDrop: (job: Job, teamId: string, date: string) => void;
  zoomLevel: 'hour' | 'day' | 'week' | 'month';
  daysToShow: number;
  rangeStart: Date;
  viewportWidth?: number; // Available width for day columns
  capacitiesByTeam: Map<string, Map<string, TeamDailyCapacity>>;
  summariesByTeam: Map<string, TeamCapacitySummary>;
  membersByTeam: Map<string, MemberCapacity[]>;
}

export function GanttView({
  jobs,
  teams,
  onUpdateJob,
  onJobClick,
  onDrop,
  zoomLevel,
  daysToShow,
  rangeStart,
  viewportWidth,
  capacitiesByTeam,
  summariesByTeam,
  membersByTeam,
}: GanttViewProps) {
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const toggleTeamExpand = useCallback((teamId: string) => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  }, []);

  // Detect scheduling conflicts across all teams (still client-side — it
  // needs the job-level start/end times, which BE capacity doesn't expose).
  const conflicts = useMemo(() => {
    const conflictsByTeam = new Map<string, ReturnType<typeof detectSchedulingConflicts>>();

    teams.forEach((team) => {
      const teamJobs = jobs.filter((job) => job.teamId === team.id && job.scheduledDate);
      const teamConflicts = detectSchedulingConflicts(teamJobs);
      if (teamConflicts.size > 0) {
        conflictsByTeam.set(team.id, teamConflicts);
      }
    });

    return conflictsByTeam;
  }, [jobs, teams]);

  // Per-zoom minimum column widths chosen so job IDs + duration labels remain
  // legible. When viewport is wide enough we expand to fill; when too narrow
  // (e.g. Month on a small screen) we keep the minimum and let the container
  // scroll horizontally.
  const minWidthByZoom: Record<typeof zoomLevel, number> = {
    hour: 60 * 24, // 60px per hour × 24 hours
    day: 200,
    week: 120,
    month: 70,
  };

  const pixelsPerDay = useMemo(() => {
    const minWidth = minWidthByZoom[zoomLevel] ?? 80;
    if (viewportWidth && daysToShow > 0) {
      const SIDEBAR_WIDTH = 200;
      const availableWidth = viewportWidth - SIDEBAR_WIDTH;
      const fillWidth = Math.floor(availableWidth / daysToShow);
      return Math.max(fillWidth, minWidth);
    }
    return minWidth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomLevel, viewportWidth, daysToShow]);

  // Generate array of dates to display
  const dates = useMemo(() => {
    return Array.from({ length: daysToShow }, (_, i) => addDays(rangeStart, i));
  }, [rangeStart, daysToShow]);

  // Group jobs by team
  const jobsByTeam = useMemo(() => {
    const map = new Map<string, Job[]>();

    // Initialize all teams
    teams.forEach((team) => {
      map.set(team.id, []);
    });

    // Assign scheduled jobs to teams
    jobs.forEach((job) => {
      if (job.scheduledDate && job.teamId) {
        const teamJobs = map.get(job.teamId) || [];
        teamJobs.push(job);
        map.set(job.teamId, teamJobs);
      }
    });

    return map;
  }, [jobs, teams]);

  // Handle drop from sidebar or move between resources
  const handleDrop = (job: Job, resourceId: string, date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // Pass to parent which will show time picker modal
    onDrop(job, resourceId, dateStr);
  };

  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <GanttScrollProvider scrollRef={scrollRef}>
      <div className="flex-1 flex flex-col overflow-hidden bg-background">
        {/* Single horizontally-scrolling container — header + rows share scrollLeft,
            so date headers always align with the body cells beneath them. */}
        <div ref={scrollRef} className="flex-1 overflow-x-auto overflow-y-auto">
          <div className="min-w-max">
            <GanttHeader dates={dates} pixelsPerDay={pixelsPerDay} zoomLevel={zoomLevel} />

            {teams.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <div className="text-sm font-medium mb-2">No teams available</div>
                <div className="text-xs">
                  Select teams from the filters above to view the Gantt chart
                </div>
              </div>
            ) : (
              teams.map((team) => {
                const teamJobs = jobsByTeam.get(team.id) || [];
                const teamCapacity = capacitiesByTeam.get(team.id);
                const teamSummary = summariesByTeam.get(team.id);
                const teamConflicts = conflicts.get(team.id);
                const teamMembers = membersByTeam.get(team.id);

                return (
                  <GanttResourceRow
                    key={team.id}
                    resourceId={team.id}
                    resourceName={team.name}
                    jobs={teamJobs}
                    rangeStart={rangeStart}
                    pixelsPerDay={pixelsPerDay}
                    totalDays={daysToShow}
                    onDrop={handleDrop}
                    onJobClick={onJobClick}
                    dailyCapacity={teamCapacity}
                    summary={teamSummary}
                    conflicts={teamConflicts}
                    zoomLevel={zoomLevel}
                    members={teamMembers}
                    isExpanded={expandedTeams.has(team.id)}
                    onToggleExpand={() => toggleTeamExpand(team.id)}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
    </GanttScrollProvider>
  );
}
